import React, { useState, useRef } from 'react';
import { Sparkles, Upload, RefreshCw, Image as ImageIcon, Home, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { ColorImage } from './ColorImage';
import { ROOM_TYPES, roomLabel } from '../../../lib/roomTypes';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const fabricTypeBadge = (type) => {
  const styles = {
    'Solar Screen': 'bg-amber-100 text-amber-800',
    'Solar Screen Decorative': 'bg-orange-100 text-orange-800',
    'Light Filtering / Privacy / Blackout': 'bg-blue-100 text-blue-800',
    'Light Filtering': 'bg-sky-100 text-sky-800',
    'Blackout': 'bg-neutral-800 text-white',
    'Sheer': 'bg-pink-100 text-pink-800'
  };
  return styles[type] || 'bg-gray-100 text-gray-800';
};

const SceneTile = ({ room, url, active, busy, onView, onGenerate }) => (
  <div
    className={`relative rounded-lg overflow-hidden border aspect-[4/3] group ${active ? 'ring-2 ring-amber-500 border-amber-500' : url ? 'border-neutral-200' : 'border-dashed border-neutral-300 bg-neutral-50'}`}
    data-testid={`scene-tile-${room.id}`}
  >
    {url ? (
      <button type="button" className="absolute inset-0" onClick={onView} title={`View ${room.label}`} data-testid={`scene-view-${room.id}`}>
        <ColorImage src={url} alt={room.label} className="w-full h-full" />
      </button>
    ) : (
      <div className="absolute inset-0 flex items-center justify-center text-neutral-400"><Home className="w-5 h-5" /></div>
    )}
    <span className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-black/60 text-white pointer-events-none">{room.label}</span>
    <button
      type="button"
      onClick={onGenerate}
      disabled={busy}
      data-testid={`scene-generate-${room.id}`}
      title={url ? `Regenerate ${room.label}` : `Generate ${room.label}`}
      className={`absolute bottom-1 right-1 h-6 px-1.5 rounded text-[10px] font-medium flex items-center gap-1 shadow transition-opacity ${
        url ? 'bg-white/90 text-neutral-700 opacity-0 group-hover:opacity-100' : 'bg-amber-600 text-white'
      } disabled:opacity-50`}
    >
      {url ? <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /> : <Plus className="w-3 h-3" />}
      {url ? 'Redo' : 'Generate'}
    </button>
  </div>
);

// Per-swatch pop-up: swatch + every AI room variation, each generated only on explicit click.
export const ColorDetailModal = ({ color, token, onClose, onRegenerate, onGenerateMissing, regenerating, onColorUpdated }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const scenes = color?.room_visualizations || {};
  const firstRoom = color?.visualization_room_type || Object.keys(scenes)[0] || null;
  const [view, setView] = useState(() => (color?.image_url ? 'swatch' : firstRoom ? `room:${firstRoom}` : 'swatch'));

  if (!color) return null;

  const missingRooms = ROOM_TYPES.filter((r) => !scenes[r.id]);
  const viewingRoom = view.startsWith('room:') ? view.slice(5) : null;
  const activeUrl = viewingRoom ? scenes[viewingRoom] || color.room_visualization_url : color.image_url || color.room_visualization_url;

  const handleSwatchUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API_URL}/admin/fabrics/colors/${color.id}/swatch`, form, { headers: { Authorization: `Bearer ${token}` } });
      onColorUpdated({ ...color, image_url: data.image_url });
      setView('swatch');
      toast.success('Swatch uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Swatch upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl" data-testid="color-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-6 h-6 rounded-full border-2" style={{ backgroundColor: color.hex_color || '#ccc' }} />
            <span data-testid="color-detail-name">{color.name}</span>
            <span className="text-sm font-normal text-neutral-500">{color.brand_name} · {color.collection_name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div className="md:col-span-3 space-y-3">
            <div className="aspect-[4/3] rounded-lg overflow-hidden bg-neutral-100">
              <ColorImage key={activeUrl} src={activeUrl} alt={color.name} hex={color.hex_color} className="w-full h-full" eager fit={viewingRoom ? 'cover' : 'contain'} />
            </div>
            <div className="flex items-center justify-between text-xs text-neutral-500">
              <span className="flex items-center gap-1" data-testid="detail-view-caption">
                {viewingRoom ? <><Sparkles className="w-3 h-3" /> AI-generated {roomLabel(viewingRoom).toLowerCase()} visualization</> : <><ImageIcon className="w-3 h-3" /> Original fabric swatch</>}
              </span>
              <Button size="sm" variant={view === 'swatch' ? 'default' : 'ghost'} className="h-7 gap-1.5 text-xs" onClick={() => setView('swatch')} disabled={!color.image_url} data-testid="detail-view-swatch-btn">
                <ImageIcon className="w-3.5 h-3.5" /> Swatch
              </Button>
            </div>

            <div className="pt-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-wide text-neutral-500">Room scenes · {ROOM_TYPES.length - missingRooms.length}/{ROOM_TYPES.length}</p>
                {missingRooms.length > 0 && (
                  <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs border-amber-300 text-amber-800 hover:bg-amber-50" disabled={regenerating} onClick={() => onGenerateMissing(color.id, missingRooms.map((r) => r.id))} data-testid="generate-missing-scenes-btn">
                    <Sparkles className="w-3.5 h-3.5" /> Generate {missingRooms.length} missing
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2" data-testid="scene-grid">
                {ROOM_TYPES.map((room) => (
                  <SceneTile
                    key={room.id}
                    room={room}
                    url={scenes[room.id]}
                    active={viewingRoom === room.id}
                    busy={regenerating}
                    onView={() => setView(`room:${room.id}`)}
                    onGenerate={() => onRegenerate(color.id, room.id)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="md:col-span-2 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs text-neutral-500 uppercase tracking-wide">Brand</label><p className="font-medium">{color.brand_name}</p></div>
              <div><label className="text-xs text-neutral-500 uppercase tracking-wide">Collection</label><p className="font-medium">{color.collection_name}</p></div>
            </div>
            <div>
              <label className="text-xs text-neutral-500 uppercase tracking-wide">Fabric Type</label>
              <div className="flex flex-wrap gap-1 mt-1">
                <Badge className={fabricTypeBadge(color.fabric_type)}>{color.fabric_type}</Badge>
                {color.openness && <Badge variant="outline">Openness {color.openness}</Badge>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs text-neutral-500 uppercase tracking-wide">Color Code</label><p className="font-mono">{color.code || '-'}</p></div>
              <div>
                <label className="text-xs text-neutral-500 uppercase tracking-wide">Hex Color</label>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded border" style={{ backgroundColor: color.hex_color || '#ccc' }} />
                  <code className="text-sm">{color.hex_color || '-'}</code>
                </div>
              </div>
            </div>
            <div className="pt-4 border-t">
              <label className="text-xs text-neutral-500 uppercase tracking-wide">Status</label>
              <div className="flex gap-2 mt-1">
                <Badge variant={color.is_active ? 'default' : 'secondary'}>{color.is_active ? 'Active' : 'Inactive'}</Badge>
                <Badge variant={color.is_visible_online ? 'default' : 'secondary'}>{color.is_visible_online ? 'Visible Online' : 'Hidden'}</Badge>
              </div>
            </div>
            <div className="pt-4 border-t space-y-2">
              <label className="text-xs text-neutral-500 uppercase tracking-wide">Swatch</label>
              <Button variant="outline" className="w-full gap-2 justify-start" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-swatch-btn">
                <Upload className={`w-4 h-4 ${uploading ? 'animate-bounce' : ''}`} />
                {uploading ? 'Uploading…' : color.image_url ? 'Replace Fabric Swatch' : 'Upload Fabric Swatch'}
              </Button>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleSwatchUpload} data-testid="upload-swatch-input" />
              <p className="text-[11px] text-neutral-500">Room scenes are rendered from this swatch — upload the real fabric file for the most faithful results.</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ColorDetailModal;
